import React from "react";
import { CalendarClock, Radio, Trash2, Video } from "lucide-react";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Avatar, AvatarImage, AvatarFallback } from "@/components/ui/avatar";

function whenLabel(iso) {
  const d = new Date(iso);
  const diffMin = Math.round((d.getTime() - Date.now()) / 60000);
  const abs = d.toLocaleString("pt-BR", { weekday: "short", day: "2-digit", month: "2-digit", hour: "2-digit", minute: "2-digit" });
  if (diffMin <= 0) return { abs, rel: "começa agora", soon: true };
  if (diffMin < 60) return { abs, rel: `em ${diffMin} min`, soon: diffMin <= 10 };
  if (diffMin < 1440) return { abs, rel: `em ${Math.round(diffMin / 60)} h`, soon: false };
  return { abs, rel: `em ${Math.round(diffMin / 1440)} dia(s)`, soon: false };
}

export default function ScheduledLivesRow({ items, onStart, onCancel }) {
  if (!items?.length) return null;
  return (
    <Card className="p-4 overflow-hidden" data-testid="scheduled-lives-row">
      <div className="flex items-center gap-2 mb-3">
        <CalendarClock className="w-4 h-4 text-red-500" />
        <div className="text-sm font-bold uppercase tracking-wider text-red-500">Lives agendadas</div>
        <div className="text-xs text-muted-foreground">· {items.length}</div>
      </div>
      <div className="flex gap-3 overflow-x-auto pb-1">
        {items.map((s) => {
          const w = whenLabel(s.scheduled_at);
          return (
            <div
              key={s.scheduled_id}
              data-testid={`scheduled-live-${s.scheduled_id}`}
              className={`flex-shrink-0 w-64 rounded-xl border p-3 space-y-2 ${w.soon ? "border-red-500/60 bg-red-500/5" : "border-border"}`}
            >
              <div className="flex items-center gap-2">
                <Avatar className="w-8 h-8">
                  <AvatarImage src={s.streamer_picture} />
                  <AvatarFallback>{s.streamer_name?.[0]}</AvatarFallback>
                </Avatar>
                <div className="min-w-0 flex-1">
                  <div className="text-xs font-semibold truncate" data-testid={`scheduled-live-title-${s.scheduled_id}`}>{s.title}</div>
                  <div className="text-[10px] text-muted-foreground truncate">{s.is_mine ? "Você" : s.streamer_name}</div>
                </div>
                {s.record && (
                  <span title="Será gravada" className="text-[9px] font-bold uppercase text-red-500 flex items-center gap-1"><Video className="w-3 h-3" /> REC</span>
                )}
              </div>
              <div className="text-[11px]">
                <span className="font-semibold">{w.abs}</span>
                <span className={`ml-1.5 px-1.5 py-0.5 rounded-full text-[10px] font-bold ${w.soon ? "bg-red-500 text-white" : "bg-muted text-muted-foreground"}`} data-testid={`scheduled-live-rel-${s.scheduled_id}`}>{w.rel}</span>
              </div>
              {s.is_mine ? (
                <div className="flex gap-2">
                  <Button size="sm" data-testid={`scheduled-live-start-${s.scheduled_id}`} className="flex-1 rounded-full bg-red-500 hover:bg-red-600 text-white h-8" onClick={() => onStart?.(s)}>
                    <Radio className="w-3.5 h-3.5 mr-1.5" /> Iniciar agora
                  </Button>
                  <Button size="sm" variant="outline" data-testid={`scheduled-live-cancel-${s.scheduled_id}`} className="rounded-full h-8 px-2.5" onClick={() => onCancel?.(s)} aria-label="Cancelar agendamento">
                    <Trash2 className="w-3.5 h-3.5" />
                  </Button>
                </div>
              ) : (
                <div className="text-[10px] text-muted-foreground">Você receberá um lembrete 10 min antes.</div>
              )}
            </div>
          );
        })}
      </div>
    </Card>
  );
}
